import { useAnalytics } from "@codat/analytics";
import {
  BankIcon,
  CartIcon,
  CircleEmptyQuestionIcon,
  ClipboardCopy,
  EntityInfo,
  FileIcon,
  InvoiceIcon,
  LinkedIcon,
  RightArrowIcon,
  useToast,
} from "@codat/orchard-ui";
import { DataConnection } from "api/public";
import classNames from "classnames";
import * as React from "react";
import { Collapse } from "react-collapse";
import formatUrl from "utils/formatUrl";

import { paths } from "components/App/paths";
import Button from "components/Button";
import ClipboardInput from "components/ClipboardInput";
import { ConnectionStatusPill } from "components/ConnectionStatusPill";

import styles from "./LinkModal.module.scss";

const Icon = ({ sourceType }: { sourceType: string | null | undefined }) => {
  switch (sourceType) {
    case "Accounting":
      return (
        <div
          className={classNames(styles.connectionIcon, styles.accountingIcon)}
        >
          <InvoiceIcon />
        </div>
      );
    case "Commerce":
      return (
        <div className={classNames(styles.connectionIcon, styles.commerceIcon)}>
          <CartIcon />
        </div>
      );
    case "Banking":
      return (
        <div className={classNames(styles.connectionIcon, styles.bankingIcon)}>
          <BankIcon />
        </div>
      );
    case "Other":
      return (
        <div className={classNames(styles.connectionIcon, styles.fileIcon)}>
          <FileIcon />
        </div>
      );
    default:
      return (
        <div
          className={classNames(styles.connectionIcon, styles.accountingIcon)}
        >
          <CircleEmptyQuestionIcon />
        </div>
      );
  }
};

export const Connection: React.FC<{
  connection: DataConnection;
  companyId: string;
  showLinkUrl: boolean;
  onToggleLinkUrl: (id: string) => void;
}> = ({ connection, companyId, showLinkUrl, onToggleLinkUrl }) => {
  const { addToast } = useToast();
  const { sendEvent } = useAnalytics();

  return (
    <li
      id={connection.id}
      className={styles.connectionItem}
      data-testid="connection-item"
    >
      <div className={styles.container}>
        <div className={styles.connection}>
          <Icon sourceType={connection.sourceType} />
          <div>
            <EntityInfo
              title={connection.platformName}
              subtitle={
                <div className={styles.connectionId}>
                  <span>{connection.id}</span>{" "}
                  <ClipboardCopy
                    className={styles.connectionIdCopyBtn}
                    label="Copy company id"
                    data-testid="companies-table-copy-id-button"
                    iconClassName={classNames(styles.copyButton, styles.hover)}
                    onCopy={() => {
                      addToast({
                        message: "Copied connection ID to clipboard",
                        variant: "success",
                      });
                    }}
                    onClick={() => {
                      sendEvent("Connection ID - Copy", {
                        connectionId: connection.id,
                      });
                    }}
                    noBackground
                    text={connection.id}
                    color="interface"
                  />
                </div>
              }
              data-testid="companies-table-name"
            />
          </div>
          <div className={styles.connectionStatus}>
            {connection.status && (
              <ConnectionStatusPill status={connection.status} />
            )}
          </div>
          <div className={styles.connectionButtons}>
            <Button
              onClick={() => onToggleLinkUrl(connection.id)}
              label="Show link URL"
              size="small"
              data-testid="link-modal-connection-view-url"
              buttonName="Show Link URL"
              color="interface"
              variant="secondary"
              eventProperties={{ companyId, connectionId: connection.id }}
            >
              <LinkedIcon />
            </Button>
            <Button
              label="Go to connection"
              size="small"
              data-testid="link-modal-connection-view"
              color="interface"
              variant="secondary"
              buttonName="View Connection"
              to={formatUrl(paths.companies.CONNECTIONS, {
                companyId,
              })}
              eventProperties={{ companyId, connectionId: connection.id }}
            >
              <RightArrowIcon />
            </Button>
          </div>
          <div
            className={classNames(styles.connectionLinkInput, {
              [styles.open]: showLinkUrl,
            })}
            data-testid="link-modal-connection-collapse"
          >
            <Collapse isOpened={showLinkUrl}>
              <ClipboardInput
                label="Show Connection link URL"
                id="connection-link-url"
                text={connection.linkUrl}
                onCopy={() => {
                  addToast({
                    message: "Copied connection link URL to clipboard",
                    variant: "success",
                  });
                }}
                inputName="Connection Link URL"
                eventProperties={{ companyId, connectionId: connection.id }}
              />
            </Collapse>
          </div>
        </div>
      </div>
    </li>
  );
};
